import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PaymentStatus } from '@models/appointments/payment-status';
import { Clinic } from '@models/clinic';
import { Patient } from '@models/patient';
import { PaymentProcessorType } from '@models/payments/payment-processor-type.enum';
import { PreviousTreatment } from '@models/treatment-planning/previous-treatment';
import { ActionPanelService } from '@services/actionpanel.service';
import { AppointmentService } from '@services/appointments.service';
import { BreakpointService } from '@services/breakpoint.service';
import { ClinicsService } from '@services/clinics.service';
import { FormatterService } from '@services/formatter.service';
import { PatientService } from '@services/patient.service';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-patient-profile-tab',
  templateUrl: './patient-profile-tab.component.html',
  styleUrls: ['./patient-profile-tab.component.less'],
})
export class PatientProfileTabComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  get thePatient(): Patient {
    return this.patientService.patientPanelPatient;
  }
  set thePatient(value: Patient) {
    this.patientService.patientPanelPatient = value;
    this.patientService.thePatientHasBeenUpdated(value);
  }

  @ViewChild('panelContainer', { static: true }) panelContainer: ElementRef;

  panelHeight: number;
  loading = false;
  disableGrid = false;
  mobileView = true;
  unsub: Subject<void> = new Subject<void>();
  previousAppointments: PreviousTreatment[];
  futureAppointments: PreviousTreatment[];
  patientId: number;
  clinic: Clinic;

  AppointmentPaymentStatus = PaymentStatus;
  PaymentProcessorType = PaymentProcessorType;

  constructor(
    public patientService: PatientService,
    public formatterService: FormatterService,
    private appointmentService: AppointmentService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private actionPanelService: ActionPanelService,
    private breakpointService: BreakpointService,
    private clinicsService: ClinicsService
  ) {}

  ngOnInit() {
    this.breakpointService.mobileBreakpoint$.pipe(takeUntil(this.unsub)).subscribe((mobileView) => {
      this.mobileView = mobileView;
    });

    this.patientId = +this.route.snapshot.params.patId.split('_')[0];
    if (this.clinicsService.clinic) {
      this.clinic = this.clinicsService.clinic;
    }
    this.actionPanelService
      .actionPanelVisible()
      .pipe(takeUntil(this.unsub))
      .pipe(take(1))
      .subscribe(() => {
        this.adjustComponentHeights();
      });

    this.getPreviousTreatmentAppointments();
    this.getFutureTreatmentAppointments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.adjustComponentHeights();
  }

  ngAfterViewInit() {
    this.adjustComponentHeights();
  }

  public formatVisitDate(treatment: PreviousTreatment) {
    return moment(treatment.date).add(moment.duration(treatment.startTime)).format('YYYY-MM-DD h:mma');
  }

  public formatCancellationDate(treatment: PreviousTreatment) {
    if (treatment.cancellationDate === null) {
      return '';
    }

    return moment(treatment.cancellationDate).format('YYYY-MM-DD h:mma');
  }

  public getChargeAmount(treatment: PreviousTreatment) {
    if (
      treatment.cancellationCharge &&
      treatment.reasonCancellationNotCharged &&
      treatment.reasonCancellationNotCharged != ''
    ) {
      return `$0 / $${treatment.cancellationCharge}`;
    } else return `$${treatment.cancellationCharge}`;
  }

  private getPreviousTreatmentAppointments() {
    this.loading = true;
    this.appointmentService
      .getTreatmentAppointmentsInDateRange(this.patientId, undefined, undefined, new Date(new Date().toDateString()))
      .pipe(takeUntil(this.unsub))
      .subscribe((appointments: PreviousTreatment[]) => {
        this.previousAppointments = appointments.filter((appt) => {
          let startTime = moment(appt.date).startOf('day').add(moment.duration(appt.startTime)).toDate();
          let curTime = new Date();

          return startTime <= curTime;
        });
        this.loading = false;
      });
  }

  private getFutureTreatmentAppointments() {
    this.loading = true;
    this.appointmentService
      .getTreatmentAppointmentsInDateRange(this.patientId, undefined, new Date(new Date().toDateString()))
      .pipe(takeUntil(this.unsub))
      .subscribe((appointments: PreviousTreatment[]) => {
        this.futureAppointments = appointments
          .filter((appt) => {
            let startTime = moment(appt.date).startOf('day').add(moment.duration(appt.startTime)).toDate();
            let curTime = new Date();

            return startTime > curTime;
          })
          .reverse();
        this.loading = false;
      });
  }

  private adjustComponentHeights() {
    if (this.panelContainer !== null && this.panelContainer !== undefined) {
      this.panelHeight = this.panelContainer.nativeElement.offsetHeight; //50px padding top/bottom and 16px padding between
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
